<template>
  <v-flex class="icon-select">
    <v-select
      :items="options"
      :item-value="optionValue"
      :value="selectedValue"
      :placeholder="placeholdertext"
      :disabled="disabled"
      @change="emitSelected($event)"
      solo
      :menu-props="{ contentClass: 'icon-select-menu' }"
    >
    <template slot="selection" slot-scope="data">
      <base-base64-icon 
        class="icon-select-icon" 
        :class="{ disabled: disabled }"
        :base64Icon="data.item[optionIcon]"
      />
      <div 
        class="icon-select-label"
        :class="{ disabled: disabled }"
      > 
        {{ data.item[optionLabel] }}
      </div>
     </template>
      <template slot="item" slot-scope="data">
        <base-base64-icon 
          class="icon-select-icon" 
          :base64Icon="data.item[optionIcon]"
        />
        <v-list-tile-content>
          <v-list-tile-title class="icon-select-label"> 
            {{ data.item[optionLabel] }}
          </v-list-tile-title>
        </v-list-tile-content>
      </template>
    </v-select>
  </v-flex>
</template>


<script lang="ts">
import Vue from 'vue'

import BaseBase64Icon from '../base/BaseBase64Icon.vue'

export default Vue.extend({
  name: 'IconSelect',
  components: {
    BaseBase64Icon,
  },
  props: {
    /**
     * array of strings or objects to be used by the drop down menu
     */
    options: {
      type: Array,
      default: [],
    },
    /**
     * the selected value
     */
    selectedValue: {
      type: String,
      default: '',
    },
    /**
     * the property name of the value string inside the options object
     */
    optionValue: {
      type: String,
      default: '',
    },
    /**
     * property name of the displayed icon as base64 string inside the options object
     */
    optionIcon: {
      type: String,
      default: '',
    },
    /**
     * property name of the displayed string inside the options object
     */
    optionLabel: {
      type: String,
      default: '',
    },
    /**
     * disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * optional placeholder text, displayed in case nothing is selected
     */
    placeholdertext: {
      type: String,
      default: '',
    },
  },
  methods: {
    emitSelected(value: string): void {
      /**
       * Selected event.
       *
       * @event selected
       * @type {string}
       */
      this.$emit('selected', value)
    },
  },
})
</script>
<style lang="scss">
.icon-select {
  margin: 0px;
  padding: 10px;
}
.icon-select > .v-text-field.v-text-field--solo .v-input__control {
  min-height: 30px;
}
.icon-select > .v-text-field--solo>.v-input__control>.v-input__slot {
  background-color: transparent !important;
  box-shadow: none !important;
}
.icon-select > .v-input > .v-input__control > .v-text-field__details {
  display: none;
}
.icon-select > .v-select .v-select__selections {
  padding: 0px 5px;
  border-width: $default-border-width;
  border-style: solid;
  border-color: $fore-color-primary;
  color: $fore-color-primary !important;
  font-family: $font-family-primary;
  &.disabled {
    color: $fore-color-disabled !important;
  }
}
.icon-select .v-select__selection--disabled {
    color: $fore-color-disabled !important;
}
.icon-select > .v-text-field.v-text-field--enclosed>.v-input__control>.v-input__slot {
  color: $fore-color-primary;
  padding: 0px;
}
.icon-select .v-input {
  font-size: inherit;
}
.icon-select .v-icon {
  color: $fore-color-primary !important;
}
.icon-select ::placeholder {
  color: $fore-color-disabled !important;
}
.icon-select-label {
  padding-left: 5px;
  &.disabled {
    color: $fore-color-disabled !important;
  }
}
.icon-select-icon {
  color: $fore-color-primary;
  &.disabled {
    opacity: 0.4;
  }
}
.icon-select-menu .v-list {
  background: $bg-primary !important;
}
.icon-select-menu .v-list .v-list__tile--active.primary--text {
  color: $fore-color-primary !important;
  font-weight: bold;
  font-size: inherit;
}
.icon-select-menu .v-list .v-list__tile {
  color: $fore-color-primary !important;
  font-size: inherit;
}
</style>

<docs>
An drop down with icon support

Usage example
```js
new Vue({
  data: function() {
    return {
      options: [
        {id: "id1", label: "label1", icon:"data:image/svg+xml;base64,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"},
        {id: "id2", label: "label2"}
      ],
      selectedValue: 'id1',
    }
  },
  methods: {
    onSelected(value) {
      console.log(value)
      this.selectedValue = value
    }
  },
  template: `<v-app style="height: 48px;background:transparent">
    <icon-select
      :options="options"
      :option-value="'id'"
      :option-label="'label'"
      :option-icon="'icon'"
      :selected-value="selectedValue"
      @selected="onSelected"/>
  </v-app>`
})
```

Disabled
```jsx
<v-app style="height: 48px;background:transparent">
  <icon-select
    :options="[{id: 'id1', label: 'label1'}]"
    :option-value="'id'"
    :option-label="'label'"
    :option-icon="'icon'"
    selected-value="id1"
    disabled
  />
</v-app>
```

Placeholder text
```jsx
<v-app style="height: 48px;background:transparent">
  <icon-select
    :options="[{id: '1', label:'label1'}]"
    :option-value="'id'"
    :option-label="'label'"
    :option-icon="'icon'"
    placeholdertext="Select something please!"
  />
</v-app>
```
</docs>
